import { selectedJobs, yards as yardsStore, jobsListStore } from './stores'
import { validateYardImages, validateYardImage, encodePolyline } from './helpers.js';
import { toE164 } from './phoneUtils.js';

if (typeof TT_WEB_API_URL === "undefined" || !TT_WEB_API_URL) {
    throw new Error("Backend API URL is not configured. Please set the environment variable.");
}

/**
 * Calculates scheduled_date (YYYY-MM-DD) by taking the job start_date
 * and adding in days (scheduled_day - 1).
 */
export function calculateScheduledDate(startDate, scheduledDay = 1) {
    if (!startDate) return null;
    try {
        let dayNum = 1;
        if (Array.isArray(scheduledDay)) {
            dayNum = scheduledDay[0] || 1;
        } else if (scheduledDay != null) {
            dayNum = parseInt(scheduledDay, 10) || 1;
        }
        const offsetDays = Math.max(0, dayNum - 1);

        const cleanStr = String(startDate).split('T')[0].split(' ')[0].trim();
        const parts = cleanStr.split('-').map(Number);
        if (parts.length !== 3 || isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2])) {
            return cleanStr;
        }

        const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
        d.setUTCDate(d.getUTCDate() + offsetDays);

        const yyyy = d.getUTCFullYear();
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    } catch (err) {
        console.warn("[calculateScheduledDate] Failed to calculate date:", err);
        return String(startDate).split('T')[0];
    }
}

export let activeJobs = []
export let yardMaterials = []
export let yards = [];
export let filteredYards = []

/**
 * Fetches jobs directly from MongoDB via the REST API.
 * @param {string[]} status The status, as a list of strings, of the jobs to fetch.
 *                          ["active", "proposed", "pending", "completed", "cancelled"]
 * @param {object} [options] Optional configuration object (e.g. { signal, updateStore })
 */
/**
 * Fetches live jobs and real-time truck locations directly from /jobs/live/fetch.
 * Updates MONGO_CONN_WEB with live GPS pings and returns the jobs list in the exact same format as /jobs.
 * @param {object} [options] Optional configuration object (e.g. { signal, updateStore, jobNumber })
 * @returns {Promise<Object>} Object with { count: number, jobs: Array }
 */
/**
 * Returns today's date formatted as YYYY-MM-DD in local time.
 */
export function getTodayDateStr() {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

/**
 * Checks whether an allocated truck matches the target schedule date (YYYY-MM-DD).
 */
export function isTruckScheduledForDate(truck, jobStartDate, targetDateStr = null) {
    if (!truck || typeof truck !== 'object') return true;
    const target = targetDateStr || getTodayDateStr();
    if (target === 'all') return true;

    const rawDate = truck.scheduled_date || truck.scheduledDate;
    if (rawDate) {
        const cleanDate = String(rawDate).split('T')[0].split(' ')[0].trim();
        return cleanDate === target;
    }

    const day = truck.scheduled_day != null ? truck.scheduled_day : truck.day;
    if (jobStartDate && day != null) {
        const calc = calculateScheduledDate(jobStartDate, day);
        if (calc) {
            return String(calc).split('T')[0].split(' ')[0].trim() === target;
        }
    }

    return true; // For backwards compatibility with dateless test allocations
}

export async function fetchJobsLive(options={}){
    const fetchOptions = {};
    if (options && options.signal) {
        fetchOptions.signal = options.signal;
    }

    let url = TT_WEB_API_URL + '/jobs/live/fetch';
    const params = [];
    if (options && options.jobNumber) {
        params.push('job_number=' + encodeURIComponent(options.jobNumber));
    }
    if (options && options.scheduleDate) {
        params.push('schedule_date=' + encodeURIComponent(options.scheduleDate));
    }
    if (params.length > 0) {
        url += '?' + params.join('&');
    }

    let res;
    try {
        res = await fetch(url, fetchOptions);
    } catch (err) {
        if (err && err.name === 'AbortError') {
            // Request was intentionally aborted to flush pending requests
            return null;
        }
        throw err;
    }

    const jobsRes = await res.json().catch(() => ({}));

    if (res.ok) {
        // Guarantee only allocated_trucks with a schedule date matching the current date (or requested date) are returned
        const targetDate = (options && options.scheduleDate) ? options.scheduleDate : getTodayDateStr();
        if (targetDate !== 'all' && jobsRes && Array.isArray(jobsRes.jobs)) {
            jobsRes.jobs.forEach(job => {
                const jobStart = job.start_date;
                if (Array.isArray(job.materials)) {
                    job.materials.forEach(mat => {
                        const matStart = mat.start_date || jobStart;
                        if (Array.isArray(mat.allocated_trucks)) {
                            mat.allocated_trucks = mat.allocated_trucks.filter(t =>
                                isTruckScheduledForDate(t, matStart, targetDate)
                            );
                        }
                    });
                }
                if (Array.isArray(job.allocated_trucks)) {
                    job.allocated_trucks = job.allocated_trucks.filter(t =>
                        isTruckScheduledForDate(t, jobStart, targetDate)
                    );
                }
            });
        }

        if (options && options.updateStore !== false) {
            if (jobsRes && Array.isArray(jobsRes.jobs)) {
                jobsListStore.set(jobsRes.jobs);
            }
        }
        activeJobs = jobsRes;
        return jobsRes;
    } else {
        throw new Error(jobsRes.message || jobsRes.error || ('Failed to fetch live jobs from database (HTTP ' + res.status + ')'));
    }
}

export async function fetchJobs(status=[], options={}){
    if (status.length === 0){
        status = ["active", "proposed", "pending", "completed", "cancelled"];
    }
    const statusQuery = status.join(",");
    const fetchOptions = {};
    if (options && options.signal) {
        fetchOptions.signal = options.signal;
    }

    let res;
    try {
        res = await fetch(TT_WEB_API_URL + '/jobs?populate=customer&status=' + statusQuery, fetchOptions);
    } catch (err) {
        if (err && err.name === 'AbortError') {
            // Request was intentionally aborted to flush pending requests
            return null;
        }
        throw err;
    }

    const jobsRes = await res.json().catch(() => ({}));

    if (res.ok) {
        if (options && options.updateStore !== false) {
            if (jobsRes && Array.isArray(jobsRes.jobs)) {
                jobsListStore.set(jobsRes.jobs);
            }
        }
        activeJobs = jobsRes;
        return jobsRes;
    } else {
        throw new Error(jobsRes.message || jobsRes.error || `Failed to fetch jobs from database (HTTP ${res.status})`);
    }
}

/**
 * Fetches a single job by ID directly from MongoDB via the REST API.
 * @param {string|number} jobId
 */
export async function fetchJobById(jobId) {
    if (!jobId) return null;
    const strId = String(jobId);

    try {
        const res = await fetch(`${TT_WEB_API_URL}/jobs/${jobId}?populate=customer`);
        if (res.ok) {
            const data = await res.json();
            if (data && data.job) {
                return data.job;
            }
        }
    } catch (err) {
        console.warn("fetchJobById network error:", err);
    }

    // Check store as secondary
    if (activeJobs && Array.isArray(activeJobs.jobs)) {
        const found = activeJobs.jobs.find(j => String(j.id) === strId || String(j.__job_number__) === strId);
        if (found) return found;
    }
    return null;
}

/**
 * Formats and normalizes a Job payload according to the canonical tt_lib Job and Material schemas.
 * - Uses customer { name, phone, address } or customer ID
 * - Strictly removes all legacy company and company_id keys
 * - Nests material operational fields under operating_hours, fleet, and schedule
 * - Strips deprecated keys: company, company_id, target_completion_date, customer_summary, financial_summary, and flat material fields
 */
export function formatJobPayload(jobData) {
    if (!jobData) return {};
    const { company: _company, company_id: _company_id, target_completion_date, customer_summary, financial_summary, ...restJob } = jobData;

    let customerField = jobData.customer;
    if (typeof customerField === 'number' || (typeof customerField === 'string' && /^\d+$/.test(customerField))) {
        customerField = Number(customerField);
    } else if (typeof customerField === 'string' && customerField.trim()) {
        customerField = { name: customerField.trim() };
    } else if (!customerField || typeof customerField !== 'object') {
        customerField = {};
    }

    let resolvedCustomer;
    if (typeof customerField === 'number') {
        resolvedCustomer = customerField;
    } else {
        const rawClient = customerField;
        const clientPhone = rawClient.phone ? toE164(rawClient.phone) : (jobData.contacts?.[0]?.phone ? toE164(jobData.contacts[0].phone) : "");
        const clientAddress = rawClient.address || jobData.address || {};
        resolvedCustomer = {
            name: rawClient.name || "",
            phone: clientPhone,
            address: {
                street: clientAddress.street || "",
                suburb: clientAddress.suburb || "",
                state: clientAddress.state || "NSW",
                postcode: clientAddress.postcode || "",
                ...(clientAddress.coordinates ? { coordinates: clientAddress.coordinates } : {})
            }
        };
    }

    const startDate = restJob.start_date || new Date().toISOString().split("T")[0];
    const daysCount = parseInt(restJob.days_count, 10) || 1;
    const startTime = restJob.start_time || "07:00";
    const finishTime = restJob.finish_time || "17:00";
    const loadInterval = parseInt(restJob.load_interval, 10) || 15;
    const loadUhf = restJob.load_uhf_channel || "";
    const disposeUhf = restJob.dispose_uhf_channel || "";
    const siteNotes = restJob.site_notes || restJob.notes || "";
    const notes = restJob.notes || restJob.site_notes || "";

    const materials = Array.isArray(restJob.materials) ? restJob.materials.map(m => {
        const {
            target_completion_date: mTargetDate,
            geometry,
            start_time: _st,
            finish_time: _ft,
            load_mins: _lm,
            tip_mins: _tm,
            runs_per_day: _rpd,
            total_loads: _tl,
            estimated_days: _ed,
            days_single_truck: _dst,
            full_days: _fd,
            remainder_days: _rd,
            total_tolls: _tt,
            truck_day_rate: _tdr,
            day_rate: _dr,
            truck_type: _ttype,
            truck_capacity_tonne: _tcap,
            trailer_type: _trltype,
            trailer_capacity_tonne: _trlcap,
            total_capacity_tonne: _totcap,
            total_tonnes: _tottonnes,
            ...mRest
        } = m;

        const yardObj = (m.yard && typeof m.yard === 'object') ? {
            id: m.yard.id ?? m.yard_id,
            name: m.yard.name ?? m.yard_name,
            suburb: m.yard.suburb || m.yard.address?.suburb || m.suburb || "",
            image: m.yard.image || m.image || null,
            address: m.yard.address || null,
            contacts: Array.isArray(m.yard.contacts) ? m.yard.contacts : [],
            main_contact: m.yard.main_contact || null,
            is_cheapest: m.yard.is_cheapest ?? m.yard.is_cheapest_rate ?? false
        } : (m.yard != null && (typeof m.yard === 'number' || typeof m.yard === 'string')) ? m.yard : (m.yard_id ? {
            id: m.yard_id,
            name: m.yard_name || "",
            suburb: m.yard_suburb || m.suburb || "",
            image: m.image || null,
            is_cheapest: Boolean(m.is_cheapest_rate)
        } : null);

        const resolvedDisposalAddr = (() => {
            if (m.disposal_address === null && !m.dispose_address) return null;
            const raw = m.disposal_address || m.dispose_address || (typeof m.yard === 'object' ? m.yard?.address : null) || null;
            if (!raw) return null;
            if (typeof raw === 'object') {
                const coords = raw.coordinates ? {
                    lat: Number(Array.isArray(raw.coordinates) ? raw.coordinates[1] : (raw.coordinates.lat ?? raw.coordinates.latitude)),
                    lng: Number(Array.isArray(raw.coordinates) ? raw.coordinates[0] : (raw.coordinates.lng ?? raw.coordinates.longitude))
                } : null;
                return {
                    street: raw.street || raw.address || raw.place_name || null,
                    suburb: raw.suburb || m.suburb || (typeof m.yard === 'object' ? (m.yard?.suburb || m.yard?.address?.suburb) : null) || null,
                    state: raw.state || (typeof m.yard === 'object' ? m.yard?.address?.state : null) || "NSW",
                    postcode: raw.postcode || (typeof m.yard === 'object' ? m.yard?.address?.postcode : null) || null,
                    name: raw.name || raw.place_name || m.yard_name || (typeof m.yard === 'object' ? m.yard?.name : null) || null,
                    coordinates: (coords && !isNaN(coords.lat) && !isNaN(coords.lng)) ? coords : null
                };
            }
            return {
                street: String(raw),
                suburb: m.suburb || (typeof m.yard === 'object' ? (m.yard?.suburb || m.yard?.address?.suburb) : null) || null,
                state: "NSW",
                postcode: null,
                name: m.yard_name || (typeof m.yard === 'object' ? m.yard?.name : null) || null,
                coordinates: null
            };
        })();

        const resolvedLoadAddr = (() => {
            if (m.load_address === null && !m.siteAddress && !m.loadAddress) return null;
            const raw = m.load_address || m.siteAddress || m.loadAddress || restJob.load_address || restJob.address || restJob.siteAddress || null;
            if (!raw) return null;
            if (typeof raw === 'object') {
                const coords = raw.coordinates ? {
                    lat: Number(Array.isArray(raw.coordinates) ? raw.coordinates[1] : (raw.coordinates.lat ?? raw.coordinates.latitude)),
                    lng: Number(Array.isArray(raw.coordinates) ? raw.coordinates[0] : (raw.coordinates.lng ?? raw.coordinates.longitude))
                } : null;

                let parsedSuburb = raw.suburb;
                let parsedPostcode = raw.postcode;
                const fullStr = raw.street || raw.name || raw.place_name || "";
                if (!parsedSuburb && fullStr) {
                    const parts = fullStr.split(',').map(s => s.trim()).filter(Boolean);
                    if (parts.length >= 2) {
                        const subCandidate = parts[1].replace(/\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT|New South Wales|Australia)\b/gi, '').replace(/\b\d{4}\b/g, '').trim();
                        if (subCandidate.length > 1) {
                            parsedSuburb = subCandidate;
                        }
                    }
                }
                if (!parsedPostcode && fullStr) {
                    const pcMatch = fullStr.match(/\b(2\d{3}|3\d{3}|4\d{3}|5\d{3}|6\d{3}|7\d{3}|08\d{2})\b/);
                    if (pcMatch) {
                        parsedPostcode = parseInt(pcMatch[1], 10);
                    }
                }

                return {
                    street: raw.street || raw.address || raw.place_name || null,
                    suburb: parsedSuburb || restJob.suburb || restJob.address?.suburb || null,
                    state: raw.state || restJob.address?.state || "NSW",
                    postcode: parsedPostcode || restJob.address?.postcode || null,
                    name: raw.name || raw.place_name || null,
                    coordinates: (coords && !isNaN(coords.lat) && !isNaN(coords.lng)) ? coords : null
                };
            }
            return {
                street: String(raw),
                suburb: null,
                state: "NSW",
                postcode: null,
                name: null,
                coordinates: null
            };
        })();

        const truckCap = Number(m.fleet?.truck_capacity_tonne ?? m.truck_capacity_tonne ?? 19);
        const trailerCap = Number(m.fleet?.trailer_capacity_tonne ?? m.trailer_capacity_tonne ?? 19);
        const totalCap = Number(m.fleet?.total_capacity_tonne ?? (truckCap + trailerCap) ?? 38);
        const truckDayRate = Number(m.fleet?.truck_day_rate != null ? m.fleet.truck_day_rate : (m.truck_day_rate != null ? m.truck_day_rate : (m.day_rate != null ? m.day_rate : 1400)));
        const costPerTonne = m.cost_per_tonne != null ? Number(m.cost_per_tonne) : (m.financial_breakdown?.base_disposal_rate ?? null);
        const unitPrice = m.unit_price != null ? Number(m.unit_price) : (m.proposed_unit_price != null ? Number(m.proposed_unit_price) : (m.financial_breakdown?.effective_rate_per_tonne ?? null));
        const proposedUnitPrice = m.proposed_unit_price != null ? Number(m.proposed_unit_price) : unitPrice;
        const totalPrice = m.total_price != null ? Number(m.total_price) : (m.proposed_total_price != null ? Number(m.proposed_total_price) : (m.financial_breakdown?.subtotal_ex_gst ?? null));
        const proposedTotalPrice = m.proposed_total_price != null ? Number(m.proposed_total_price) : totalPrice;
        const totalCost = m.total_cost != null ? Number(m.total_cost) : (m.financial_breakdown?.subtotal_ex_gst ?? null);
        const marginPercent = m.margin_percent != null ? Number(m.margin_percent) : null;
        const runsPerDay = Number(m.schedule?.runs_per_day != null ? m.schedule.runs_per_day : (m.runs_per_day ?? 5.0));
        const totalLoads = Number(m.schedule?.total_loads != null ? m.schedule.total_loads : (m.total_loads ?? (totalCap > 0 && (m.amount || m.total_tonnes) ? Math.ceil((m.amount || m.total_tonnes) / totalCap) : 1)));
        const estimatedDays = Number(m.schedule?.estimated_days != null ? m.schedule.estimated_days : (m.estimated_days ?? 1.0));
        const totalTolls = Number(m.schedule?.total_tolls ?? m.total_tolls ?? 0);
        const loadMins = Number(m.operating_hours?.load_mins ?? m.load_mins ?? 15);
        const tipMins = Number(m.operating_hours?.tip_mins ?? m.tip_mins ?? 15);
        const startTimeVal = m.operating_hours?.start_time || m.start_time || startTime;
        const finishTimeVal = m.operating_hours?.finish_time || m.finish_time || finishTime;
        const allocTrucksCount = Number(m.schedule?.allocated_trucks ?? (Array.isArray(m.allocated_trucks) ? m.allocated_trucks.length : (m.allocated_trucks ?? 1)));

        return {
            ...mRest,
            name: m.name || m.material_name || "Material",
            amount: parseFloat(m.amount) || parseFloat(m.total_tonnes) || 0,
            status: m.status || restJob.status || "active",
            start_date: m.start_date || startDate,
            days_count: parseInt(m.days_count, 10) || daysCount,
            load_interval: parseInt(m.load_interval, 10) || loadInterval,
            load_uhf_channel: m.load_uhf_channel || loadUhf,
            dispose_uhf_channel: m.dispose_uhf_channel || disposeUhf,
            site_notes: m.site_notes || siteNotes,
            notes: m.notes || notes,
            load_address: resolvedLoadAddr,
            disposal_address: resolvedDisposalAddr,
            siteAddress: m.siteAddress || (resolvedLoadAddr?.place_name || (resolvedLoadAddr?.street ? `${resolvedLoadAddr.street}${resolvedLoadAddr.suburb ? ', ' + resolvedLoadAddr.suburb : ''}` : "")),
            dispose_address: m.dispose_address || (resolvedDisposalAddr?.place_name || (resolvedDisposalAddr?.street ? `${resolvedDisposalAddr.street}${resolvedDisposalAddr.suburb ? ', ' + resolvedDisposalAddr.suburb : ''}` : "")),
            yard: yardObj,
            yard_id: m.yard_id ?? (typeof yardObj === 'object' ? yardObj?.id : (typeof yardObj === 'number' ? yardObj : null)) ?? null,
            yard_name: m.yard_name ?? (typeof yardObj === 'object' ? yardObj?.name : null) ?? null,
            suburb: m.suburb ?? (typeof yardObj === 'object' ? (yardObj?.suburb || yardObj?.address?.suburb) : null) ?? "",
            image: m.image ?? (typeof yardObj === 'object' ? yardObj?.image : null) ?? null,
            contact_name: m.contact_name ?? m.main_contact?.name ?? (typeof yardObj === 'object' ? (yardObj?.contact_name || yardObj?.contact?.name) : "") ?? "",
            contact_phone: m.contact_phone ?? m.main_contact?.phone ?? (typeof yardObj === 'object' ? (yardObj?.phone || yardObj?.contact?.phone) : "") ?? "",
            contact_email: m.contact_email ?? m.main_contact?.email ?? (typeof yardObj === 'object' ? (yardObj?.email || yardObj?.contact?.email) : "") ?? "",
            contact_position: m.contact_position ?? m.main_contact?.position ?? "",
            main_contact: m.main_contact || (typeof yardObj === 'object' ? yardObj?.main_contact : null) || null,
            cost_per_tonne: costPerTonne,
            total_cost: totalCost,
            proposed_unit_price: proposedUnitPrice,
            unit_price: unitPrice,
            proposed_total_price: proposedTotalPrice,
            total_price: totalPrice,
            margin_percent: marginPercent,
            operating_hours: {
                start_time: startTimeVal,
                finish_time: finishTimeVal,
                load_mins: loadMins,
                tip_mins: tipMins
            },
            fleet: {
                truck_type: m.fleet?.truck_type || (truckCap === 12 ? "6 wheeler - 12t" : (truckCap === 16 ? "8 wheeler - 16t" : (truckCap === 15 ? "8 wheeler - 15t" : "10 wheeler - 19t"))),
                truck_capacity_tonne: truckCap,
                trailer_type: m.fleet?.trailer_type || (trailerCap === 0 ? "None" : "8 wheeler - 20t"),
                trailer_capacity_tonne: trailerCap,
                total_capacity_tonne: totalCap,
                truck_day_rate: truckDayRate
            },
            schedule: {
                runs_per_day: runsPerDay,
                total_loads: totalLoads,
                allocated_trucks: allocTrucksCount,
                estimated_days: estimatedDays,
                days_single_truck: Number(m.schedule?.days_single_truck ?? (runsPerDay > 0 ? (totalLoads / runsPerDay).toFixed(2) : 1)),
                full_days: Number(m.schedule?.full_days ?? Math.floor(estimatedDays)),
                remainder_days: Number(m.schedule?.remainder_days ?? 0),
                total_tolls: totalTolls
            },
            financial_breakdown: {
                base_disposal_rate: m.financial_breakdown?.base_disposal_rate ?? costPerTonne ?? 0,
                base_cost_per_load: m.financial_breakdown?.base_cost_per_load ?? (costPerTonne ? costPerTonne * totalCap : 0),
                base_day_rate: m.financial_breakdown?.base_day_rate ?? truckDayRate,
                base_cartage_cost: m.financial_breakdown?.base_cartage_cost ?? 0,
                base_tolls: m.financial_breakdown?.base_tolls ?? totalTolls,
                base_material_cost: m.financial_breakdown?.base_material_cost ?? 0,
                base_total_cost: m.financial_breakdown?.base_total_cost ?? totalCost ?? 0,
                quoted_disposal_rate: m.financial_breakdown?.quoted_disposal_rate ?? unitPrice ?? 0,
                quoted_cost_per_load: m.financial_breakdown?.quoted_cost_per_load ?? 0,
                quoted_day_rate: m.financial_breakdown?.quoted_day_rate ?? truckDayRate,
                quoted_cartage_cost: m.financial_breakdown?.quoted_cartage_cost ?? 0,
                quoted_tolls: m.financial_breakdown?.quoted_tolls ?? totalTolls,
                subtotal_ex_gst: m.financial_breakdown?.subtotal_ex_gst ?? totalPrice ?? 0,
                gst: m.financial_breakdown?.gst ?? (totalPrice ? totalPrice * 0.1 : 0),
                total_inc_gst: m.financial_breakdown?.total_inc_gst ?? (totalPrice ? totalPrice * 1.1 : 0),
                effective_rate_per_tonne: m.financial_breakdown?.effective_rate_per_tonne ?? unitPrice ?? 0,
                margin_added: m.financial_breakdown?.margin_added ?? 0,
                ...(m.financial_breakdown || {})
            },
            polyline: m.polyline || null,
            yard: yardObj,
            allocated_trucks: Array.isArray(m.allocated_trucks) ? m.allocated_trucks.map(t => {
                if (!t || typeof t !== 'object') return t;
                const rawDay = t.scheduled_day ?? (Array.isArray(t.scheduled_day) ? t.scheduled_day[0] : 1);
                const dayNum = parseInt(rawDay, 10) || 1;
                const jobOrMatStartDate = restJob.start_date || startDate;
                const calcDate = calculateScheduledDate(jobOrMatStartDate, dayNum);
                return {
                    ...t,
                    scheduled_day: dayNum,
                    scheduled_date: calcDate
                };
            }) : []
        };
    }) : [];

    return {
        ...restJob,
        customer: resolvedCustomer,
        start_date: startDate,
        days_count: daysCount,
        start_time: startTime,
        finish_time: finishTime,
        load_interval: loadInterval,
        load_uhf_channel: loadUhf,
        dispose_uhf_channel: disposeUhf,
        site_notes: siteNotes,
        notes: notes,
        materials
    };
}

/**
 * Creates a new Job directly in MongoDB via the REST API.
 * @param {Object} jobData Full Job payload to create
 * @returns {Promise<Object>} The created Job object from MongoDB
 */
export async function createJob(jobData) {
    if (!jobData) throw new Error("Job data is required to create a job.");

    const payload = formatJobPayload(jobData);

    const res = await fetch(`${TT_WEB_API_URL}/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.job) {
        throw new Error(data.error || data.message || `Failed to create job in database (HTTP ${res.status})`);
    }

    const clientData = payload.customer;
    const createdJob = {
        ...data.job,
        customer: (data.job?.customer && typeof data.job.customer === 'object')
            ? data.job.customer
            : (clientData || data.job?.customer)
    };
    delete createdJob.company;

    // Update in-memory activeJobs and jobsListStore
    if (!activeJobs || !Array.isArray(activeJobs.jobs)) {
        activeJobs = { count: 0, jobs: [] };
    }
    activeJobs.jobs = [createdJob, ...activeJobs.jobs.filter(j => j.id !== createdJob.id)];
    activeJobs.count = activeJobs.jobs.length;
    activeJobs = { ...activeJobs };
    jobsListStore.set(activeJobs.jobs);

    // Also select on map
    selectedJobs.update(ids => (ids.includes(createdJob.id) ? ids : [...ids, createdJob.id]));

    // Whenever a job that has status: 'active' is created, call the live endpoint to sync data
    if (String(createdJob.status || "").toLowerCase() === 'active') {
        const liveJobId = createdJob.id || createdJob._id || createdJob.__job_number__;
        if (liveJobId) {
            activateJobLive(liveJobId).catch(err => {
                console.warn(`[createJob] Warning syncing active job #${liveJobId} to live DB:`, err);
            });
        }
    }

    return createdJob;
}

/**
 * Updates a job directly in MongoDB via the REST API.
 * @param {Object} updatedJob Full updated Job object
 * @returns {Promise<Object>} The updated Job object from MongoDB
 */
export async function updateJob(updatedJob){
    if (!updatedJob || (!updatedJob.id && !updatedJob.__job_number__)) return updatedJob;

    const payload = formatJobPayload(updatedJob);
    const jobId = updatedJob.id || updatedJob._id || payload.id;
    const url = jobId ? `${TT_WEB_API_URL}/jobs/${jobId}` : `${TT_WEB_API_URL}/jobs`;

    let res = await fetch(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    }).catch(() => null);

    if (!res || !res.ok) {
        res = await fetch(`${TT_WEB_API_URL}/jobs`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.job) {
        throw new Error(data.error || data.message || `Failed to save job to database (HTTP ${res.status})`);
    }

    const clientData = payload.customer;
    const savedMaterials = (data.job?.materials && Array.isArray(data.job.materials) && data.job.materials.length > 0)
        ? data.job.materials.map((m, idx) => {
            const pMat = payload.materials?.[idx] || {};
            const {
                start_time: _st, finish_time: _ft, load_mins: _lm, tip_mins: _tm,
                runs_per_day: _rpd, total_loads: _tl, estimated_days: _ed,
                days_single_truck: _dst, full_days: _fd, remainder_days: _rd,
                total_tolls: _tt, truck_day_rate: _tdr, day_rate: _dr,
                truck_type: _ttype, truck_capacity_tonne: _tcap,
                trailer_type: _trltype, trailer_capacity_tonne: _trlcap,
                total_capacity_tonne: _totcap,
                ...cleanM
            } = m;
            return {
                ...cleanM,
                ...pMat,
                fleet: { ...(cleanM.fleet || {}), ...(pMat.fleet || {}) },
                schedule: { ...(cleanM.schedule || {}), ...(pMat.schedule || {}) },
                operating_hours: { ...(cleanM.operating_hours || {}), ...(pMat.operating_hours || {}) },
                financial_breakdown: { ...(cleanM.financial_breakdown || {}), ...(pMat.financial_breakdown || {}) }
            };
        })
        : payload.materials;

    const savedJob = {
        ...payload,
        ...data.job,
        customer: (data.job?.customer && typeof data.job.customer === 'object')
            ? data.job.customer
            : (clientData || data.job?.customer),
        materials: savedMaterials
    };
    delete savedJob.company;

    // Update in-memory activeJobs immediately with the authoritative MongoDB document
    if (activeJobs && Array.isArray(activeJobs.jobs)) {
        activeJobs.jobs = activeJobs.jobs.map(j => {
            if (j.id === savedJob.id || (j.__job_number__ && j.__job_number__ === savedJob.__job_number__)) {
                return savedJob;
            }
            return j;
        });
        activeJobs = { ...activeJobs };
        jobsListStore.set(activeJobs.jobs);
    }

    // Whenever a job that has status: 'active' is updated, call the live endpoint to update the data in MONGO_CONN_LIVE
    if (String(savedJob.status || payload.status || "").toLowerCase() === 'active') {
        const liveJobId = savedJob.id || savedJob._id || savedJob.__job_number__ || payload.id;
        if (liveJobId) {
            console.log(`[updateJob] Job #${liveJobId} is active - updating live tracking via POST /jobs/live...`);
            activateJobLive(liveJobId).catch(err => {
                console.warn(`[updateJob] Warning syncing active job #${liveJobId} to live DB:`, err);
            });
        }
    }

    return savedJob;
}

/**
 * Deletes a Job by ID directly in MongoDB via the REST API.
 * @param {string|number} jobId The job ID or job number
 * @returns {Promise<Object>} Deletion result confirmation
 */
export async function deleteJob(jobId) {
    if (!jobId) throw new Error("Job ID is required to delete.");

    const strId = String(jobId);
    const res = await fetch(`${TT_WEB_API_URL}/jobs/${jobId}`, {
        method: 'DELETE'
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.error || data.message || `Failed to delete job from database (HTTP ${res.status})`);
    }

    // Update in-memory stores immediately upon successful database deletion
    if (activeJobs && Array.isArray(activeJobs.jobs)) {
        activeJobs.jobs = activeJobs.jobs.filter(j => String(j.id) !== strId && String(j.__job_number__) !== strId);
        activeJobs.count = activeJobs.jobs.length;
        activeJobs = { ...activeJobs };
        jobsListStore.set(activeJobs.jobs);
    }
    selectedJobs.update(ids => ids.filter(id => String(id) !== strId));

    return data;
}

/**
 * Calls the POST /jobs/live endpoint with the job ID to embed Company and Yard
 * documents and upsert the job into the MONGO_CONN_LIVE database.
 * @param {string|number|Object} jobId The job ID, _id, job_number, or Job object
 * @returns {Promise<Object>} API response status and upserted live job
 */
export async function activateJobLive(jobId) {
    if (!jobId) throw new Error("jobId is required to activate job to live");
    const cleanId = typeof jobId === "object"
        ? (jobId.id || jobId._id || jobId.__job_number__ || jobId.job_number)
        : jobId;

    if (!cleanId) throw new Error("Could not determine a valid job ID to activate to live");

    console.log(`[activateJobLive] Calling POST ${TT_WEB_API_URL}/jobs/live with job_id:`, cleanId);

    const res = await fetch(`${TT_WEB_API_URL}/jobs/live?job_id=${encodeURIComponent(cleanId)}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ job_id: cleanId })
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.error || data.message || `Failed to activate job to live database (HTTP ${res.status})`);
    }

    console.log(`[activateJobLive] Successfully upserted Job #${cleanId} to live database:`, data);
    return data;
}

/**
 * Convenience helper to set a job status to 'active' and sync to live database.
 * @param {Object|string|number} jobOrId Job object or ID
 * @returns {Promise<Object>} Result of activateJobLive
 */
export async function activateJob(jobOrId) {
    let job = typeof jobOrId === "object" ? jobOrId : null;
    let jobId = job ? (job.id || job._id || job.__job_number__ || job.job_number) : jobOrId;

    if (!job && jobId) {
        job = await fetchJob(jobId).catch(() => null);
    }

    if (job) {
        const updated = {
            ...job,
            status: "active",
            materials: (job.materials || []).map(m => ({ ...m, status: "active" }))
        };
        await updateJob(updated).catch(err => {
            console.warn("[activateJob] Warning when updating job status in web DB:", err);
        });
    }

    return await activateJobLive(jobId);
}


export async function fetchYards(yardId=null){
    let url = TT_WEB_API_URL + '/yards?populate=company'
    if (yardId){
        url += "&id=" + yardId;
    }
    const res = await fetch(url);
    let yardsRes = await res.json();

    if (res.ok) {
        if (yardsRes.yards) {
            validateYardImages(yardsRes.yards);
            if (!yardId) {
                // Fetching all yards: update global yards and yardsStore
                yards = yardsRes.yards;
                yardMaterials = yardsRes.materials;
                if (Array.isArray(yardsRes.yards) && yardsRes.yards.length > 0) {
                    yardsStore.set(yardsRes.yards);
                }
            } else {
                // Fetching a single yard (e.g. for yard details): merge into existing list, NEVER overwrite all yards!
                if (Array.isArray(yardsRes.yards) && yardsRes.yards.length > 0) {
                    const singleYard = yardsRes.yards[0];
                    if (Array.isArray(yards)) {
                        const idx = yards.findIndex(y => String(y.id) === String(singleYard.id));
                        if (idx !== -1) {
                            yards[idx] = { ...yards[idx], ...singleYard };
                        } else {
                            yards.push(singleYard);
                        }
                    }
                    yardsStore.update((curr) => {
                        if (!curr || !Array.isArray(curr) || curr.length === 0) {
                            return [singleYard];
                        }
                        const idx = curr.findIndex(y => String(y.id) === String(singleYard.id));
                        if (idx !== -1) {
                            const updated = [...curr];
                            updated[idx] = { ...updated[idx], ...singleYard };
                            return updated;
                        }
                        return [...curr, singleYard];
                    });
                }
            }
        }
        return yardsRes.yards;
    } else {
        throw new Error(yardsRes);
    }
}

export async function saveYard(yard){
    var headers = new Headers();
    headers.append("Content-Type", "application/json");
    
    const payload = {
        ...(yard || {}),
        name: yard.name || (yard.company?.name ?? ""),
        license_number: yard.license_number ?? "",
        phone: yard.phone ? toE164(yard.phone) : "",
        company: {
            name: yard.company?.name || yard.name || ""
        },
        address: {
            street: yard.address?.street ?? "",
            suburb: yard.address?.suburb ?? "",
            state: yard.address?.state ?? "",
            postcode: yard.address?.postcode ?? "",
            coordinates: yard.address?.coordinates || null
        },
        contacts: Array.isArray(yard.contacts) ? yard.contacts.map(c => ({
            name: c.name ?? "",
            phone: c.phone ? toE164(c.phone) : "",
            email: c.email ?? "",
            position: c.position ?? "",
            main: Boolean(c.main)
        })) : [],
        accepts: Array.isArray(yard.accepts) ? yard.accepts.map(a => ({
            name: a.name ?? "",
            details: a.details ?? null,
            prices: Array.isArray(a.prices) ? a.prices.map(p => ({
                quantity: p.quantity !== undefined && p.quantity !== null && p.quantity !== "" ? (isNaN(p.quantity) ? p.quantity : Number(p.quantity)) : "",
                price: typeof p.price === 'string' && p.price.trim().toLowerCase() === 'free' ? 'free' : (p.price !== "" && !isNaN(p.price) ? Number(p.price) : p.price),
                unit: p.unit === 'bin' ? 'bin' : (p.unit === 'cube' || p.unit === 'cubes') ? 'cubes' : 'tonnes',
                last_updated: p.last_updated || new Date().toISOString(),
                ...(p.last_updated_user ? { last_updated_user: p.last_updated_user } : {})
            })) : []
        })) : []
    };
    if (yard.id !== undefined) payload.id = yard.id;
    if (yard._id !== undefined) payload._id = yard._id;
    if (yard.image) payload.image = yard.image;

    var raw = JSON.stringify(payload);

    var requestOptions = {
        method: 'POST',
        headers: headers,
        body: raw,
        redirect: 'follow'
      };

    let url = TT_WEB_API_URL + '/yards'
    
    const res = await fetch(url, requestOptions);
    let yardsRes = await res.json();

    if (res.ok) {
        if (yardsRes.yards) {
            validateYardImages(yardsRes.yards);
        }
        if (yard && yard.id) {
            const savedSingleYard = (Array.isArray(yardsRes.yards) && yardsRes.yards.length === 1)
                ? yardsRes.yards[0]
                : yard;
            if (Array.isArray(yards)) {
                const idx = yards.findIndex(y => String(y.id) === String(yard.id));
                if (idx !== -1) {
                    yards[idx] = { ...yards[idx], ...savedSingleYard };
                } else {
                    yards.push(savedSingleYard);
                }
            }
            yardsStore.update(curr => {
                if (!curr || !Array.isArray(curr) || curr.length === 0) {
                    return [savedSingleYard];
                }
                const idx = curr.findIndex(y => String(y.id) === String(yard.id));
                if (idx !== -1) {
                    const updated = [...curr];
                    updated[idx] = { ...updated[idx], ...savedSingleYard };
                    return updated;
                }
                return [...curr, savedSingleYard];
            });
        } else if (Array.isArray(yardsRes.yards) && yardsRes.yards.length > 1) {
            yards = yardsRes.yards;
            yardMaterials = yardsRes.materials;
            yardsStore.set(yardsRes.yards);
        }
        return yardsRes.yards;
    } else {
        throw new Error(yardsRes);
    }
}

export async function filterYards(byMaterial, byName) {
    let filtered = [];
    if (byMaterial) {
        yards.forEach(element => {
            if (element.accepts.find(e => e.name === byMaterial)) {
                filtered.push(element);
            }
        });
    } 
    if (byName) {
        yards.forEach(element => {
            const compName = (element.company && typeof element.company === 'object' && element.company.name)
                ? element.company.name
                : (element.name || "");
            if (compName.toLowerCase().includes(byName.toLowerCase())) {
                filtered.push(element);
            }
        });
    }
    return filtered;
}

export async function fetchCompanies(companyId=null) {
    let url = TT_WEB_API_URL + '/companies'
    if (companyId){
        url += "?id=" + companyId;
    }
    const res = await fetch(url);
    let companyRes = await res.json();

    if (res.ok) {
        if (companyId){
            return companyRes.companies[0];
        }
        return companyRes.companies;
    } else {
        throw new Error(yardsRes);
    }    
}

export async function saveCompany(company=null){
    var headers = new Headers();
    headers.append("Content-Type", "application/json");
    
    var raw = JSON.stringify(company)

    var requestOptions = {
        method: 'POST',
        headers: headers,
        body: raw,
        redirect: 'follow'
      };

    let url = TT_WEB_API_URL + '/companies'
    
    const res = await fetch(url, requestOptions);
    let companiesRes = await res.json();

    if (res.ok) {
        company = companiesRes.company;
        return company;
    } else {
        throw new Error(companiesRes);
    }
}

export async function fetchTruckLocationDates(materialId=null, truckId=null) {
    var headers = new Headers();
    headers.append("Content-Type", "application/json");
    

    var requestOptions = {
        method: 'GET',
        headers: headers,
        redirect: 'follow'
      };

    let url = TT_WEB_API_URL + '/trucks/location/dates?material_id=' + materialId + '&truck_id=' + truckId;
    
    const res = await fetch(url, requestOptions);
    let locationsRes = await res.json();

    if (res.ok) {
        return locationsRes;
    } else {
        throw new Error(locationsRes);
    }
}

export async function fetchTruckLocations(materialId=null, truckId=null, date=null) {
    var headers = new Headers();
    headers.append("Content-Type", "application/json");
    

    var requestOptions = {
        method: 'GET',
        headers: headers,
        redirect: 'follow'
      };

    let url = TT_WEB_API_URL + '/trucks/locations?material_id=' + materialId + '&truck_id=' + truckId + '&date=' + date;
    
    const res = await fetch(url, requestOptions);
    let locationsRes = await res.json();

    if (res.ok) {
        return locationsRes;
    } else {
        throw new Error(locationsRes);
    }
}

// Research-backed standard configurations for Australian heavy vehicles & tipper combinations
export const TRUCK_CONFIGS = {
    "6 wheeler": { defaultCapacity: 13, label: "6 wheeler (13t)", description: "3-axle rigid / 6x4 tipper, GVM ~23-24.5t, avg payload ~13 tonnes" },
    "8 wheeler": { defaultCapacity: 17, label: "8 wheeler (17t)", description: "4-axle rigid / twin-steer 8x4 tipper, GVM ~28.5-31t, avg payload ~17 tonnes" },
    "10 wheeler": { defaultCapacity: 21, label: "10 wheeler (21t)", description: "5-axle rigid / 10x4 or twin-steer tri-drive, GVM ~34-37.5t, avg payload ~21 tonnes" }
};

export const TRAILER_CONFIGS = {
    "4 wheeler": { defaultCapacity: 12, label: "4 wheeler (12t)", description: "2-axle dog / pig trailer, ATM ~16.5-17.5t, avg payload ~12 tonnes" },
    "6 wheeler": { defaultCapacity: 16, label: "6 wheeler (16t)", description: "3-axle dog trailer (Super Dog), ATM ~21-25t, avg payload ~16 tonnes" },
    "8 wheeler": { defaultCapacity: 20, label: "8 wheeler (20t)", description: "4-axle dog trailer (Quad Dog), ATM ~28-33t, avg payload ~20 tonnes" },
    "10 wheeler": { defaultCapacity: 24, label: "10 wheeler (24t)", description: "5-axle dog trailer (Quin Dog / PBS), ATM ~35-40t, avg payload ~24 tonnes" }
};

export function getDefaultFleet() {
    return [
        { id: "TEST01", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-01", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST01" }, trailer: { id: "TRL-01", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST01" }, total_capacity: 37, default_driver: { name: "Driver VENM 1" } },
        { id: "TEST02", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-02", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST02" }, trailer: { id: "TRL-02", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST02" }, total_capacity: 37, default_driver: { name: "Driver VENM 2" } },
        { id: "TEST03", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-03", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST03" }, trailer: { id: "TRL-03", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST03" }, total_capacity: 37, default_driver: { name: "Driver VENM 3" } },
        { id: "TEST04", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-04", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST04" }, trailer: { id: "TRL-04", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST04" }, total_capacity: 37, default_driver: { name: "Driver VENM 4" } },
        { id: "TEST05", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-05", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST05" }, trailer: { id: "TRL-05", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST05" }, total_capacity: 37, default_driver: { name: "Driver VENM 5" } },
        { id: "TEST06", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-06", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST06" }, trailer: { id: "TRL-06", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST06" }, total_capacity: 37, default_driver: { name: "Driver VENM 6" } },
        { id: "TEST07", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-07", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST07" }, trailer: { id: "TRL-07", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST07" }, total_capacity: 37, default_driver: { name: "Driver VENM 7" } },
        { id: "TEST08", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-08", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST08" }, trailer: { id: "TRL-08", type: "8 wheeler", capacity: 20, attached_to_truck_id: "TEST08" }, total_capacity: 37, default_driver: { name: "Driver VENM 8" } },
        { id: "TEST09", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver GSW 1" } },
        { id: "TEST10", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver GSW 2" } },
        { id: "TEST11", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver GSW 3" } },
        { id: "TEST12", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver GSW 4" } },
        { id: "TEST13", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver GSW 5" } },
        { id: "TEST14", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver GSW 6" } },
        { id: "TEST15", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver GSW 7" } },
        { id: "TEST16", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver GSW 8" } },
        { id: "TEST17", type: "10 wheeler", capacity: 21, default_trailer: { id: "TRL-17", type: "10 wheeler", capacity: 24, attached_to_truck_id: "TEST17" }, trailer: { id: "TRL-17", type: "10 wheeler", capacity: 24, attached_to_truck_id: "TEST17" }, total_capacity: 45, default_driver: { name: "Driver Concrete 1" } },
        { id: "TEST18", type: "10 wheeler", capacity: 21, default_trailer: { id: "TRL-18", type: "10 wheeler", capacity: 24, attached_to_truck_id: "TEST18" }, trailer: { id: "TRL-18", type: "10 wheeler", capacity: 24, attached_to_truck_id: "TEST18" }, total_capacity: 45, default_driver: { name: "Driver Concrete 2" } },
        { id: "TEST19", type: "10 wheeler", capacity: 21, default_trailer: { id: "TRL-19", type: "10 wheeler", capacity: 24, attached_to_truck_id: "TEST19" }, trailer: { id: "TRL-19", type: "10 wheeler", capacity: 24, attached_to_truck_id: "TEST19" }, total_capacity: 45, default_driver: { name: "Driver Concrete 3" } },
        { id: "TEST20", type: "10 wheeler", capacity: 21, default_trailer: { id: "TRL-20", type: "10 wheeler", capacity: 24, attached_to_truck_id: "TEST20" }, trailer: { id: "TRL-20", type: "10 wheeler", capacity: 24, attached_to_truck_id: "TEST20" }, total_capacity: 45, default_driver: { name: "Driver Concrete 4" } },
        { id: "TEST21", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-21", type: "6 wheeler", capacity: 16, attached_to_truck_id: "TEST21" }, trailer: { id: "TRL-21", type: "6 wheeler", capacity: 16, attached_to_truck_id: "TEST21" }, total_capacity: 33, default_driver: { name: "Driver Concrete 5" } },
        { id: "TEST22", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-22", type: "6 wheeler", capacity: 16, attached_to_truck_id: "TEST22" }, trailer: { id: "TRL-22", type: "6 wheeler", capacity: 16, attached_to_truck_id: "TEST22" }, total_capacity: 33, default_driver: { name: "Driver Concrete 6" } },
        { id: "TEST23", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-23", type: "6 wheeler", capacity: 16, attached_to_truck_id: "TEST23" }, trailer: { id: "TRL-23", type: "6 wheeler", capacity: 16, attached_to_truck_id: "TEST23" }, total_capacity: 33, default_driver: { name: "Driver Concrete 7" } },
        { id: "TEST24", type: "8 wheeler", capacity: 17, default_trailer: { id: "TRL-24", type: "6 wheeler", capacity: 16, attached_to_truck_id: "TEST24" }, trailer: { id: "TRL-24", type: "6 wheeler", capacity: 16, attached_to_truck_id: "TEST24" }, total_capacity: 33, default_driver: { name: "Driver Concrete 8" } },
        { id: "TEST25", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver Asphalt 1" } },
        { id: "TEST26", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver Asphalt 2" } },
        { id: "TEST27", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver Asphalt 3" } },
        { id: "TEST28", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver Asphalt 4" } },
        { id: "TEST29", type: "6 wheeler", capacity: 13, default_trailer: null, trailer: null, total_capacity: 13, default_driver: { name: "Driver Asphalt 5" } }
    ];
}

export function getCustomTrucks() {
    try {
        const stored = localStorage.getItem('tt_custom_trucks');
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        return [];
    }
}

export function saveCustomTruck(truck) {
    if (!truck || !truck.id) return;
    const customs = getCustomTrucks().filter(t => String(t.id).toUpperCase() !== String(truck.id).toUpperCase());
    customs.unshift(truck);
    try {
        localStorage.setItem('tt_custom_trucks', JSON.stringify(customs));
    } catch (e) {
        console.warn("Could not save custom truck to localStorage:", e);
    }
}

export async function fetchTrucks() {
    const customTrucks = getCustomTrucks();
    let apiTrucks = [];
    try {
        const res = await fetch(TT_WEB_API_URL + '/trucks');
        if (res.ok) {
            const data = await res.json();
            if (data && Array.isArray(data.trucks) && data.trucks.length > 0) {
                apiTrucks = data.trucks.map(t => {
                    // Normalize legacy types if present
                    let tType = t.type || "8 wheeler";
                    const cleanT = tType.toLowerCase();
                    if (cleanT.includes("boggie") || cleanT.includes("bogie")) tType = "6 wheeler";
                    else if (cleanT.includes("truck & dog") || cleanT.includes("dog")) tType = "8 wheeler";

                    const truckCap = t.capacity || TRUCK_CONFIGS[tType]?.defaultCapacity || 17;
                    const trl = t.default_trailer || t.trailer;
                    const trlCap = trl?.capacity || 0;
                    return {
                        ...t,
                        type: tType,
                        capacity: truckCap,
                        default_trailer: trl || null,
                        trailer: trl || null,
                        total_capacity: t.total_capacity || (truckCap + trlCap)
                    };
                });
            }
        }
    } catch (e) {
        console.warn("fetchTrucks API error:", e);
    }

    if (apiTrucks.length === 0) {
        apiTrucks = getDefaultFleet();
    }

    // Merge custom trucks: custom trucks appear first
    if (customTrucks.length > 0) {
        const customIds = new Set(customTrucks.map(t => String(t.id).toUpperCase()));
        const nonDuplicateApi = apiTrucks.filter(t => !customIds.has(String(t.id).toUpperCase()));
        return [...customTrucks, ...nonDuplicateApi];
    }

    return apiTrucks;
}

export async function fetchTrailers(availableOnly = false) {
    try {
        const res = await fetch(`${TT_WEB_API_URL}/trailers?available=${availableOnly}`);
        if (res.ok) {
            const data = await res.json();
            if (data && Array.isArray(data.trailers)) {
                return data.trailers;
            }
        }
    } catch (e) {
        console.warn("fetchTrailers error:", e);
    }
    return [];
}

export async function createTrailer(trailerData) {
    if (!trailerData || !trailerData.id) {
        throw new Error("Trailer ID or Registration is required.");
    }
    const trlId = String(trailerData.id).trim().toUpperCase();
    const trlType = trailerData.type || "8 wheeler";
    const trlCapacity = Number(trailerData.capacity) || TRAILER_CONFIGS[trlType]?.defaultCapacity || 20;

    const payload = {
        id: trlId,
        type: trlType,
        capacity: trlCapacity
    };

    const res = await fetch(`${TT_WEB_API_URL}/trailers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });

    if (res.ok) {
        const data = await res.json();
        return data.trailer;
    } else {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Failed to create trailer.");
    }
}

export async function createTruck(truckData) {
    if (!truckData || !truckData.id) {
        throw new Error("Truck ID or Registration is required.");
    }
    const truckId = String(truckData.id).trim().toUpperCase();
    const truckType = truckData.type || "8 wheeler";
    const truckCapacity = Number(truckData.capacity) || TRUCK_CONFIGS[truckType]?.defaultCapacity || 17;

    let attachedTrailer = null;
    const inputTrailer = truckData.default_trailer || truckData.trailer;
    if (inputTrailer && inputTrailer.id) {
        const trlId = String(inputTrailer.id).trim().toUpperCase();
        const trlType = inputTrailer.type || "8 wheeler";
        const trlCapacity = Number(inputTrailer.capacity) || TRAILER_CONFIGS[trlType]?.defaultCapacity || 20;
        attachedTrailer = {
            id: trlId,
            type: trlType,
            capacity: trlCapacity,
            attached_to_truck_id: truckId
        };
    }

    const totalCap = truckCapacity + (attachedTrailer ? attachedTrailer.capacity : 0);

    const formattedTruck = {
        id: truckId,
        _id: truckId,
        type: truckType,
        capacity: truckCapacity,
        total_capacity: totalCap,
        default_trailer: attachedTrailer,
        trailer: attachedTrailer,
        default_driver: truckData.default_driver || {
            name: truckData.driver_name || "Driver",
            phone: truckData.driver_phone || ""
        }
    };

    // Cache locally immediately so user sees it right away
    saveCustomTruck(formattedTruck);

    // Persist to backend API
    try {
        const res = await fetch(TT_WEB_API_URL + '/trucks', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formattedTruck)
        });
        if (res.ok) {
            const data = await res.json();
            if (data && data.truck) {
                const trl = data.truck.default_trailer || data.truck.trailer;
                if (trl !== undefined) {
                    data.truck.default_trailer = trl;
                    data.truck.trailer = trl;
                }
                saveCustomTruck(data.truck);
                return data.truck;
            }
        } else {
            const errData = await res.json().catch(() => ({}));
            const msg = errData.message || errData.error || errData.detail;
            console.warn("Backend create truck response:", msg);
            if (msg) throw new Error(msg);
        }
    } catch (err) {
        console.warn("Could not save truck to backend:", err);
        throw err;
    }

    return formattedTruck;
}

export async function updateTruck(truckId, truckData) {
    if (!truckId) throw new Error("Truck ID is required to update.");
    const cleanId = String(truckId).trim().toUpperCase();
    const url = `${TT_WEB_API_URL}/trucks/${encodeURIComponent(cleanId)}`;
    const payload = { ...truckData };
    if (payload.default_trailer !== undefined && payload.trailer === undefined) {
        payload.trailer = payload.default_trailer;
    } else if (payload.trailer !== undefined && payload.default_trailer === undefined) {
        payload.default_trailer = payload.trailer;
    }
    try {
        const res = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (res.ok) {
            const data = await res.json();
            const updated = data.truck || data;
            const trl = updated.default_trailer || updated.trailer;
            if (trl !== undefined) {
                updated.default_trailer = trl;
                updated.trailer = trl;
            }
            saveCustomTruck(updated);
            return updated;
        } else {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.message || errData.error || errData.detail || "Failed to update truck");
        }
    } catch (err) {
        console.warn("Could not update truck on backend:", err);
        throw err;
    }
}




export async function fetchRouteLoads(requestData){
    var headers = new Headers();
    headers.append("Content-Type", "application/json");
    
    var raw = JSON.stringify(requestData)

    var requestOptions = {
        method: 'POST',
        headers: headers,
        body: raw,
        redirect: 'follow'
      };

    let url = TT_WEB_API_URL + '/route/loads'
    
    try {
        let res = await fetch(url, requestOptions);
        let route_loads_res = await res.json();

        if (res.ok) {
            return route_loads_res;
        } else {
            throw new Error(JSON.stringify(route_loads_res));
        }
    } catch (e) {
        console.error("Failed to fetch route loads:", e);
        return { error: "Failed to fetch route loads. Please check the logs." };
    }
}

export const QUOTES_API_URL = typeof TT_WEB_API_URL !== "undefined" && TT_WEB_API_URL
    ? TT_WEB_API_URL
    : (() => { throw new Error("Backend API URL is not configured. Please set the environment variable."); })();

/**
 * Saves a customer quote (creates via POST or updates existing via PUT).
 * Conforms to OpenAPI specification at /quotes.
 * @param {Object} quote The customer quote object from customerQuote store
 * @returns {Promise<Object>} The saved quote document from the API
 */
export async function saveQuote(quote) {
    if (!quote) throw new Error("No quote data provided to save.");

    const packages = (quote.haulagePackages && quote.haulagePackages.length > 0)
        ? quote.haulagePackages
        : (quote.haulagePackage ? [quote.haulagePackage] : []);

    function applyMarginVal(base, m) {
        if (!base || isNaN(base)) return 0;
        if (!m || m.value === null || m.value === "" || isNaN(m.value)) return base;
        const val = Number(m.value);
        return m.mode === "percent" ? base * (1 + val / 100) : base + val;
    }

    function sanitizeMargin(m) {
        if (!m) return { value: 0, mode: "percent" };
        const num = (m.value !== "" && m.value !== null && !isNaN(Number(m.value))) ? Number(m.value) : 0;
        return { value: num, mode: m.mode || "percent" };
    }

    let combinedBaseCost = 0;
    let combinedMarginAdded = 0;
    let combinedSubtotal = 0;
    let combinedTonnes = 0;

    const quote_materials = packages.map(pkg => {
        const tonnes = Number(pkg.totalTonnes || pkg.tonnesDeliveredInDay || pkg.totalCapacity || 1);
        const baseTonneRate = Number(pkg.tonneRate || 0);
        const baseCostPerLoad = Number(pkg.costPerLoad || 0);
        const baseDayRate = Number(pkg.dayRate || 0);
        const truckCap = Number(pkg.truckCapacityTonne || 19);
        const trailerCap = Number(pkg.trailerCapacityTonne != null ? pkg.trailerCapacityTonne : 19);
        const totalCapacity = Number(pkg.totalCapacity || (truckCap + trailerCap) || 1);
        const runs = Number(pkg.runs || 1);
        const loads = (tonnes > 0 && totalCapacity > 0)
            ? Math.ceil(tonnes / totalCapacity)
            : ((pkg.totalLoads != null && pkg.totalLoads > 0) ? Number(pkg.totalLoads) : (runs || 1));
        const allocTrucks = Math.max(1, parseInt(pkg.allocatedTrucks, 10) || 1);

        // Turnaround / duration calculations for cartage cost
        const totalDays1Truck = (runs > 0 && loads > 0) ? (loads / runs) : Number(pkg.totalDays || 0);
        const calcDays = (runs > 0 && loads > 0 && allocTrucks > 0) ? (loads / (runs * allocTrucks)) : (totalDays1Truck > 0 ? (totalDays1Truck / allocTrucks) : 0);
        const fullD = Math.floor(calcDays);
        const rem = calcDays - fullD;
        const remD = rem > 0.0001 ? (rem <= 0.50001 ? 0.5 : 1.0) : 0;

        // Base costs
        const baseCartage = (allocTrucks * fullD * baseDayRate) + (baseDayRate * remD);
        const baseMaterial = tonnes * baseTonneRate;
        const baseTolls = (pkg.tollPerLoad != null && pkg.tollPerLoad > 0)
            ? Number((pkg.tollPerLoad * loads).toFixed(2))
            : (pkg.totalTolls != null
                ? (pkg.totalLoads && Number(pkg.totalLoads) > 0 && Number(pkg.totalLoads) !== loads
                    ? Number(((Number(pkg.totalTolls) / Number(pkg.totalLoads)) * loads).toFixed(2))
                    : Number(pkg.totalTolls))
                : 0);
        const baseTotal = baseCartage + baseMaterial + baseTolls;

        const margins = pkg.margins || quote.margins || {
            tonneRate: { value: 0, mode: "percent" },
            costPerLoad: { value: 0, mode: "percent" },
            dayRate: { value: 0, mode: "percent" },
            cartageCost: { value: 0, mode: "percent" },
            totalTolls: { value: 0, mode: "percent" },
            totalCost: { value: Number(quote.marginPercent || 15), mode: "percent" }
        };

        const mTR = margins.tonneRate;
        const mCPL = margins.costPerLoad;
        const mDR = margins.dayRate;
        const mCC = margins.cartageCost;
        const mTL = margins.totalTolls;
        const mTC = margins.totalCost;

        const hasIndiv = (mTR && Number(mTR.value) !== 0) ||
                         (mCPL && Number(mCPL.value) !== 0) ||
                         (mDR && Number(mDR.value) !== 0) ||
                         (mCC && Number(mCC.value) !== 0) ||
                         (mTL && Number(mTL.value) !== 0);

        const totalRatio = (mTC && mTC.mode === "percent")
            ? (Number(mTC.value || 0) / 100)
            : (baseTotal > 0 ? (Number(mTC?.value || 0) / baseTotal) : 0);

        const adjTonneRate = (mTR && Number(mTR.value) !== 0)
            ? applyMarginVal(baseTonneRate, mTR)
            : (baseTonneRate * (1 + (hasIndiv ? 0 : totalRatio)));

        const adjCostPerLoad = (mCPL && Number(mCPL.value) !== 0)
            ? applyMarginVal(baseCostPerLoad, mCPL)
            : ((mTR && Number(mTR.value) !== 0)
                ? adjTonneRate * totalCapacity
                : (baseCostPerLoad * (1 + (hasIndiv ? 0 : totalRatio))));

        const adjDayRate = (mDR && Number(mDR.value) !== 0)
            ? applyMarginVal(baseDayRate, mDR)
            : (baseDayRate * (1 + (hasIndiv ? 0 : totalRatio)));

        const adjCartageCost = (mCC && Number(mCC.value) !== 0)
            ? applyMarginVal(baseCartage, mCC)
            : ((mDR && Number(mDR.value) !== 0)
                ? ((allocTrucks * fullD * adjDayRate) + (adjDayRate * remD))
                : (baseCartage * (1 + (hasIndiv ? 0 : totalRatio))));

        const adjTolls = (mTL && Number(mTL.value) !== 0)
            ? applyMarginVal(baseTolls, mTL)
            : (baseTolls * (1 + (hasIndiv ? 0 : totalRatio)));

        const intermediate = hasIndiv
            ? ((mTR && Number(mTR.value) !== 0
                ? (adjTonneRate * tonnes)
                : (mCPL && Number(mCPL.value) !== 0
                    ? (adjCostPerLoad * loads)
                    : baseMaterial)) + adjCartageCost + adjTolls)
            : baseTotal;

        const finalSubtotal = (mTC && Number(mTC.value) !== 0 && hasIndiv)
            ? applyMarginVal(intermediate, mTC)
            : (hasIndiv ? intermediate : applyMarginVal(baseTotal, mTC));

        const matGst = finalSubtotal * 0.1;
        const matTotalIncGst = finalSubtotal + matGst;
        const unitPriceIncGst = tonnes > 0 ? (matTotalIncGst / tonnes) : 0;
        const matMarginAdded = Math.max(0, finalSubtotal - baseTotal);

        combinedBaseCost += baseTotal;
        combinedMarginAdded += matMarginAdded;
        combinedSubtotal += finalSubtotal;
        combinedTonnes += tonnes;

        const safeUnitPrice = (isNaN(unitPriceIncGst) || !isFinite(unitPriceIncGst)) ? 0 : Number(unitPriceIncGst.toFixed(2));
        const safeTotalPrice = (isNaN(finalSubtotal) || !isFinite(finalSubtotal)) ? 0 : Number(finalSubtotal.toFixed(2));
        const safeQuantity = (isNaN(tonnes) || !isFinite(tonnes) || Math.round(tonnes) <= 0) ? 1 : Math.round(tonnes);

        const resolvedLoadAddr = (() => {
            if (pkg.load_address === null && !pkg.siteAddress) return null;
            const rawAddr = pkg.load_address || pkg.siteAddress || null;
            if (!rawAddr) return null;
            if (typeof rawAddr === "object") {
                const sc = rawAddr.coordinates || pkg.loadCoordinates || pkg.load_coordinates || quote.siteCoordinates || quote.loadAddresCoordinate || null;
                let lat = null, lng = null;
                if (Array.isArray(sc) && sc.length >= 2) { lng = Number(sc[0]); lat = Number(sc[1]); }
                else if (sc && sc.lat != null && sc.lng != null) { lat = Number(sc.lat); lng = Number(sc.lng); }
                const placeName = rawAddr.place_name || rawAddr.name || (rawAddr.street ? `${rawAddr.street}${rawAddr.suburb ? ', ' + rawAddr.suburb : ''}` : null);
                return {
                    street: rawAddr.street || (typeof rawAddr === "string" ? rawAddr : ""),
                    suburb: rawAddr.suburb || pkg.siteSuburb || null,
                    state: rawAddr.state || pkg.siteState || "NSW",
                    postcode: rawAddr.postcode != null ? (isNaN(Number(rawAddr.postcode)) ? rawAddr.postcode : Number(rawAddr.postcode)) : (pkg.sitePostcode || null),
                    name: rawAddr.name || rawAddr.place_name || null,
                    place_name: placeName,
                    coordinates: (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) ? { lat, lng } : null
                };
            }
            const rawStr = String(rawAddr).trim();
            if (!rawStr) return null;
            const sc = pkg.loadCoordinates || pkg.load_coordinates || quote.siteCoordinates || quote.loadAddresCoordinate || null;
            let lat = null, lng = null;
            if (Array.isArray(sc) && sc.length >= 2) { lng = Number(sc[0]); lat = Number(sc[1]); }
            else if (sc && sc.lat != null && sc.lng != null) { lat = Number(sc.lat); lng = Number(sc.lng); }
            const parts = rawStr.split(',').map(s => s.trim()).filter(Boolean);
            return {
                street: parts[0] || rawStr,
                suburb: parts[1] || pkg.siteSuburb || null,
                state: pkg.siteState || "NSW",
                postcode: pkg.sitePostcode || null,
                name: rawStr,
                place_name: rawStr,
                coordinates: (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) ? { lat, lng } : null
            };
        })();

        const resolvedDisposalAddr = (() => {
            if (pkg.disposal_address === null && !pkg.dispose_address) return null;
            const raw = pkg.disposal_address || pkg.dispose_address || (typeof pkg.yard === 'object' ? pkg.yard?.address : null) || null;
            if (!raw) return null;
            if (typeof raw === 'object') {
                const rawCoords = raw.coordinates || pkg.disposal_coordinates || (typeof pkg.yard === 'object' ? (pkg.yard?.address?.coordinates || pkg.yard?.coordinates) : null) || pkg.yardCoordinates || null;
                let lat = null, lng = null;
                if (Array.isArray(rawCoords) && rawCoords.length >= 2) {
                    lng = Number(rawCoords[0]);
                    lat = Number(rawCoords[1]);
                } else if (rawCoords && rawCoords.lat != null && rawCoords.lng != null) {
                    lat = Number(rawCoords.lat);
                    lng = Number(rawCoords.lng);
                }
                const coords = (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) ? { lat, lng } : null;
                const placeName = raw.place_name || raw.name || (raw.street ? `${raw.street}${raw.suburb ? ', ' + raw.suburb : ''}` : null);
                return {
                    street: raw.street || raw.address || (typeof placeName === 'string' ? placeName.split(',')[0].trim() : null) || null,
                    suburb: raw.suburb || pkg.suburb || (typeof pkg.yard === 'object' ? (pkg.yard?.suburb || pkg.yard?.address?.suburb) : null) || null,
                    state: raw.state || (typeof pkg.yard === 'object' ? pkg.yard?.address?.state : null) || "NSW",
                    postcode: raw.postcode != null ? (isNaN(Number(raw.postcode)) ? raw.postcode : Number(raw.postcode)) : (typeof pkg.yard === 'object' ? pkg.yard?.address?.postcode : null),
                    name: raw.name || raw.place_name || pkg.yardName || (typeof pkg.yard === 'object' ? pkg.yard?.name : null) || null,
                    place_name: placeName,
                    coordinates: coords
                };
            }
            const rawStr = String(raw).trim();
            if (!rawStr) return null;
            const parts = rawStr.split(',').map(s => s.trim()).filter(Boolean);
            return {
                street: parts[0] || rawStr,
                suburb: parts[1] || pkg.suburb || (typeof pkg.yard === 'object' ? (pkg.yard?.suburb || pkg.yard?.address?.suburb) : null) || null,
                state: "NSW",
                postcode: null,
                name: pkg.yardName || (typeof pkg.yard === 'object' ? pkg.yard?.name : null) || rawStr,
                place_name: rawStr,
                coordinates: null
            };
        })();

        return {
            material_name: pkg.material || "General Waste",
            quantity: safeQuantity,
            total_tonnes: tonnes,
            yard: {
                id: pkg.yardId || (typeof pkg.yard === 'object' ? pkg.yard?.id : pkg.yard) || null,
                name: pkg.yardName || (typeof pkg.yard === 'object' ? pkg.yard?.name : null) || null,
                suburb: pkg.suburb || (typeof pkg.yard === 'object' ? (pkg.yard?.suburb || pkg.yard?.address?.suburb) : null) || null,
                image: pkg.image || (typeof pkg.yard === 'object' ? pkg.yard?.image : null) || null,
                address: resolvedDisposalAddr || (typeof pkg.yard === 'object' ? pkg.yard?.address : null) || null,
                is_cheapest: !!pkg.isCheapest,
                main_contact: pkg.main_contact || (typeof pkg.yard === 'object' ? pkg.yard?.main_contact : null) || null,
                contacts: (typeof pkg.yard === 'object' && Array.isArray(pkg.yard?.contacts)) ? pkg.yard.contacts : []
            },
            yard_id: pkg.yardId || (typeof pkg.yard === 'object' ? pkg.yard?.id : pkg.yard) || null,
            yard_name: pkg.yardName || (typeof pkg.yard === 'object' ? pkg.yard?.name : null) || null,
            suburb: pkg.suburb || (typeof pkg.yard === 'object' ? (pkg.yard?.suburb || pkg.yard?.address?.suburb) : null) || "",
            image: pkg.image || (typeof pkg.yard === 'object' ? pkg.yard?.image : null) || null,
            contact_name: pkg.contact_name || pkg.main_contact?.name || (typeof pkg.yard === 'object' ? pkg.yard?.contact_name : "") || "",
            contact_phone: pkg.contact_phone || pkg.main_contact?.phone || (typeof pkg.yard === 'object' ? pkg.yard?.phone : "") || "",
            contact_email: pkg.contact_email || pkg.main_contact?.email || (typeof pkg.yard === 'object' ? pkg.yard?.email : "") || "",
            contact_position: pkg.contact_position || pkg.main_contact?.position || "",
            main_contact: pkg.main_contact || (typeof pkg.yard === 'object' ? pkg.yard?.main_contact : null) || null,
            operating_hours: {
                start_time: pkg.operating_hours?.start_time ?? pkg.startTime ?? "07:00",
                finish_time: pkg.operating_hours?.finish_time ?? pkg.finishTime ?? "17:00",
                load_mins: pkg.operating_hours?.load_mins ?? pkg.load_mins ?? (pkg.loadTime != null ? pkg.loadTime : 15),
                tip_mins: pkg.operating_hours?.tip_mins ?? pkg.tip_mins ?? (pkg.tipTime != null ? pkg.tipTime : 15)
            },
            fleet: {
                truck_type: pkg.fleet?.truck_type || pkg.truckType || (truckCap === 12 ? "6 wheeler - 12t" : (truckCap === 16 ? "8 wheeler - 16t" : (truckCap === 15 ? "8 wheeler - 15t" : "10 wheeler - 19t"))),
                truck_capacity_tonne: Number(pkg.fleet?.truck_capacity_tonne ?? truckCap),
                trailer_type: pkg.fleet?.trailer_type || pkg.trailerType || (trailerCap === 0 ? "None" : (trailerCap === 12 ? "4 wheeler - 12t" : (trailerCap === 15 ? "6 wheeler - 15t" : "8 wheeler - 20t"))),
                trailer_capacity_tonne: Number(pkg.fleet?.trailer_capacity_tonne ?? trailerCap),
                total_capacity_tonne: Number(pkg.fleet?.total_capacity_tonne ?? totalCapacity),
                truck_day_rate: Number(pkg.fleet?.truck_day_rate ?? baseDayRate)
            },
            schedule: {
                runs_per_day: Number(pkg.schedule?.runs_per_day ?? runs),
                total_loads: Number(pkg.schedule?.total_loads ?? loads),
                allocated_trucks: Number(pkg.schedule?.allocated_trucks ?? allocTrucks),
                estimated_days: Number(pkg.schedule?.estimated_days ?? Number(calcDays.toFixed(2))),
                days_single_truck: Number(pkg.schedule?.days_single_truck ?? Number(totalDays1Truck.toFixed(2))),
                full_days: Number(pkg.schedule?.full_days ?? fullD),
                remainder_days: Number(pkg.schedule?.remainder_days ?? remD),
                total_tolls: Number(pkg.schedule?.total_tolls ?? pkg.totalTolls ?? Number(baseTolls.toFixed(2)))
            },
            margins: {
                tonne_rate: sanitizeMargin(mTR),
                cost_per_load: sanitizeMargin(mCPL),
                day_rate: sanitizeMargin(mDR),
                cartage_cost: sanitizeMargin(mCC),
                total_tolls: sanitizeMargin(mTL),
                total_cost: sanitizeMargin(mTC)
            },
            financial_breakdown: {
                base_disposal_rate: Number(pkg.financial_breakdown?.base_disposal_rate ?? baseTonneRate),
                base_cost_per_load: Number(pkg.financial_breakdown?.base_cost_per_load ?? baseCostPerLoad),
                base_day_rate: Number(pkg.financial_breakdown?.base_day_rate ?? baseDayRate),
                base_cartage_cost: Number(pkg.financial_breakdown?.base_cartage_cost ?? Number(baseCartage.toFixed(2))),
                base_tolls: Number(pkg.financial_breakdown?.base_tolls ?? Number(baseTolls.toFixed(2))),
                base_material_cost: Number(pkg.financial_breakdown?.base_material_cost ?? Number(baseMaterial.toFixed(2))),
                base_total_cost: Number(pkg.financial_breakdown?.base_total_cost ?? Number(baseTotal.toFixed(2))),
                quoted_disposal_rate: Number(pkg.financial_breakdown?.quoted_disposal_rate ?? Number(adjTonneRate.toFixed(2))),
                quoted_cost_per_load: Number(pkg.financial_breakdown?.quoted_cost_per_load ?? Number(adjCostPerLoad.toFixed(2))),
                quoted_day_rate: Number(pkg.financial_breakdown?.quoted_day_rate ?? Number(adjDayRate.toFixed(2))),
                quoted_cartage_cost: Number(pkg.financial_breakdown?.quoted_cartage_cost ?? Number(adjCartageCost.toFixed(2))),
                quoted_tolls: Number(pkg.financial_breakdown?.quoted_tolls ?? Number(adjTolls.toFixed(2))),
                subtotal_ex_gst: Number(pkg.financial_breakdown?.subtotal_ex_gst ?? Number(finalSubtotal.toFixed(2))),
                gst: Number(pkg.financial_breakdown?.gst ?? Number(matGst.toFixed(2))),
                total_inc_gst: Number(pkg.financial_breakdown?.total_inc_gst ?? Number(matTotalIncGst.toFixed(2))),
                effective_rate_per_tonne: Number(pkg.financial_breakdown?.effective_rate_per_tonne ?? safeUnitPrice),
                margin_added: Number(pkg.financial_breakdown?.margin_added ?? Number(matMarginAdded.toFixed(2)))
            },
            proposed_unit_price: safeUnitPrice,
            proposed_total_price: safeTotalPrice,
            load_address: resolvedLoadAddr,
            disposal_address: resolvedDisposalAddr,
            dispose_address: resolvedDisposalAddr,
            polyline: (() => {
                let poly = pkg.polyline || pkg.load?.polyline || null;
                if (poly && typeof poly === "object") {
                    if (Array.isArray(poly.coordinates)) poly = encodePolyline(poly.coordinates);
                    else poly = null;
                }
                if (!poly && pkg.coordinates && Array.isArray(pkg.coordinates) && pkg.coordinates.length > 0) {
                    poly = encodePolyline(pkg.coordinates);
                }
                if (!poly && pkg.load?.coordinates && Array.isArray(pkg.load.coordinates) && pkg.load.coordinates.length > 0) {
                    poly = encodePolyline(pkg.load.coordinates);
                }
                if (!poly) {
                    const sc = quote.siteCoordinates || quote.loadAddresCoordinate || pkg.loadCoordinates;
                    const yc = pkg.yard?.address?.coordinates || pkg.yard?.coordinates || pkg.yardCoordinates;
                    const sLng = Array.isArray(sc) ? sc[0] : sc?.lng;
                    const sLat = Array.isArray(sc) ? sc[1] : sc?.lat;
                    const yLng = Array.isArray(yc) ? yc[0] : yc?.lng;
                    const yLat = Array.isArray(yc) ? yc[1] : yc?.lat;
                    if (sLng != null && sLat != null && yLng != null && yLat != null) {
                        poly = encodePolyline([[Number(sLng), Number(sLat)], [Number(yLng), Number(yLat)]]);
                    }
                }
                return poly || null;
            })(),
            load: {
                ...pkg,
                load_address: resolvedLoadAddr,
                disposal_address: resolvedDisposalAddr,
                dispose_address: resolvedDisposalAddr ? (resolvedDisposalAddr.place_name || resolvedDisposalAddr.street) : null,
                polyline: pkg.polyline || pkg.load?.polyline || null,
                margins: margins,
                allocatedTrucks: allocTrucks,
                adjustedRates: {
                    tonneRate: adjTonneRate,
                    costPerLoad: adjCostPerLoad,
                    dayRate: adjDayRate,
                    cartageCost: adjCartageCost,
                    totalTolls: adjTolls,
                    subtotal: finalSubtotal,
                    unitPriceIncGst: safeUnitPrice
                }
            }
        };
    });

    // Ensure load.polyline matches the resolved top-level polyline
    quote_materials.forEach(m => {
        if (m.polyline && m.load) {
            m.load.polyline = m.polyline;
        }
    });

    const combinedGst = combinedSubtotal * 0.1;
    const grandTotalIncGst = combinedSubtotal + combinedGst;
    const averageRatePerTonne = combinedTonnes > 0 ? (grandTotalIncGst / combinedTonnes) : 0;

    const quoteId = quote.id ? parseInt(quote.id, 10) : null;
    const isUpdate = quoteId !== null && !isNaN(quoteId);
    const method = isUpdate ? 'PUT' : 'POST';
    let url = `${QUOTES_API_URL}/quotes`;

    const payload = {
        quote_number: isUpdate ? (quote.quote_number || quote.quoteNumber || `QT-${quoteId}`) : null,
        quote_date: quote.quoteDate || null,
        valid_until: quote.validUntil || null,
        status: (quote.status || "draft").toLowerCase(),
        notes: quote.notes || null,
        customer: quote.selectedCompany?.id
            ? (typeof quote.selectedCompany.id === 'string' && /^\d+$/.test(quote.selectedCompany.id) ? Number(quote.selectedCompany.id) : quote.selectedCompany.id)
            : (quote.selectedCompany
                ? {
                    name: quote.selectedCompany.name || quote.customerName || "Unnamed Customer",
                    address: quote.companyAddress || quote.selectedCompany.address || null,
                    phone: quote.companyPhone || quote.selectedCompany.phone || quote.phone || null,
                    ABN: quote.selectedCompany.ABN || null,
                    ACN: quote.selectedCompany.ACN || null
                }
                : {
                    name: (quote.customerName && quote.customerName.trim()) ? quote.customerName.trim() : "Unnamed Customer",
                    contact_person: quote.contactPerson || quote.prepared_for || null,
                    phone: quote.phone || null,
                    email: quote.email || null
                }),
        customer_summary: {
            name: (quote.customerName && quote.customerName.trim()) ? quote.customerName.trim() : (quote.selectedCompany?.name || "Unnamed Customer"),
            address: quote.companyAddress || quote.selectedCompany?.address || null,
            phone: quote.phone || quote.companyPhone || quote.selectedCompany?.phone || null,
            contact_person: quote.contactPerson || quote.prepared_for || null,
            email: quote.email || null
        },
        financial_summary: {
            total_tonnes: combinedTonnes,
            combined_base_cost: Number(combinedBaseCost.toFixed(2)),
            total_margin_added: Number(combinedMarginAdded.toFixed(2)),
            subtotal_ex_gst: Number(combinedSubtotal.toFixed(2)),
            gst: Number(combinedGst.toFixed(2)),
            grand_total_inc_gst: Number(grandTotalIncGst.toFixed(2)),
            average_rate_per_tonne: Number(averageRatePerTonne.toFixed(2))
        },
        quote_materials,
        contact: {
            name: quote.contactPerson || quote.prepared_for || null,
            email: quote.email || null,
            phone: quote.phone || null,
            position: quote.position || quote.contactPosition || null
        },
        rep: null,
        margin_percent: Number(quote.margins?.totalCost?.value || quote.marginPercent || 0)
    };

    if (isUpdate) {
        payload.id = quoteId;
        url += `?id=${quoteId}`;
    }

    // Output clean, structured console logs showing exactly what fields are being sent to /quotes
    console.group(`%c[Quotes API Request] ${method} ${url}`, 'color: #0284c7; font-weight: bold; font-size: 13px;');
    console.log("Request Summary:", {
        method,
        url,
        quote_id: payload.id,
        quote_number: payload.quote_number,
        customer: typeof payload.customer === 'object' ? payload.customer?.name : payload.customer,
        customer_summary: payload.customer_summary?.name,
        phone: payload.customer_summary?.phone,
        email: payload.customer_summary?.email,
        materials_count: payload.quote_materials?.length || 0,
        materials_load_addresses: payload.quote_materials?.map(m => m.load_address?.street || "none"),
        subtotal_ex_gst: payload.financial_summary?.subtotal_ex_gst,
        gst: payload.financial_summary?.gst,
        grand_total_inc_gst: payload.financial_summary?.grand_total_inc_gst,
        margin_percent: payload.margin_percent
    });
    console.log("Full Payload sent to API:", payload);
    if (payload.quote_materials && payload.quote_materials.length > 0) {
        console.table(payload.quote_materials.map((m, idx) => ({
            "#": idx + 1,
            "Material": m.material_name,
            "Quantity (t)": m.quantity,
            "Total Tonnes": m.total_tonnes,
            "Yard Name": m.yard?.name,
            "Yard Suburb": m.dispose_address?.suburb,
            "Has Polyline": !!m.polyline,
            "Polyline Len": m.polyline ? String(m.polyline).length : 0,
            "Polyline Preview": m.polyline ? (String(m.polyline).substring(0, 35) + "...") : "NULL",
            "Has Load Addr": !!m.load_address?.street,
            "Has Disp Addr": !!m.dispose_address?.name,
            "Alloc Trucks": m.schedule?.allocated_trucks,
            "Runs/Day": m.schedule?.runs_per_day,
            "Est Days": m.schedule?.estimated_days,
            "Total Tolls ($)": m.schedule?.total_tolls,
            "Unit Price ($)": m.proposed_unit_price,
            "Total Price ($)": m.proposed_total_price
        })));
    }
    console.groupEnd();

    const headers = new Headers();
    headers.append("Content-Type", "application/json");

    let res;
    try {
        if (isUpdate) {
            const putIdUrl = `${QUOTES_API_URL}/quotes/${quoteId}`;
            res = await fetch(putIdUrl, {
                method: 'PUT',
                headers: headers,
                body: JSON.stringify(payload)
            }).catch(() => null);
        }
        if (!res || !res.ok) {
            res = await fetch(url, {
                method: method,
                headers: headers,
                body: JSON.stringify(payload)
            });
        }
    } catch (networkErr) {
        console.group(`%c[Quotes API Network Error] ${method} ${url}`, 'color: #dc2626; font-weight: bold;');
        console.error("Network fetch failed:", networkErr);
        console.groupEnd();
        throw networkErr;
    }

    let resJson;
    try {
        resJson = await res.json();
    } catch (parseErr) {
        console.group(`%c[Quotes API Error] ${method} ${url} (HTTP ${res.status})`, 'color: #dc2626; font-weight: bold;');
        console.error("Failed to parse JSON response:", parseErr);
        console.groupEnd();
        throw new Error(`Server returned HTTP ${res.status}: ${res.statusText}`);
    }

    if (res.ok) {
        console.group(`%c[Quotes API Response] ${method} ${url} (HTTP ${res.status} OK)`, 'color: #16a34a; font-weight: bold;');
        console.log("Saved Quote received from MongoDB:", resJson.quote || resJson);
        console.groupEnd();
        return resJson.quote || resJson;
    } else {
        console.group(`%c[Quotes API Error] ${method} ${url} (HTTP ${res.status})`, 'color: #dc2626; font-weight: bold;');
        console.error("API error response:", resJson);
        console.groupEnd();
        const detail = resJson?.detail;
        let errorMsg = "";
        if (Array.isArray(detail)) {
            errorMsg = detail.map(d => `${d.loc ? d.loc.join('.') + ': ' : ''}${d.msg}`).join("; ");
        } else {
            errorMsg = resJson.message || detail || (typeof resJson === 'string' ? resJson : JSON.stringify(resJson));
        }
        throw new Error(errorMsg || `API error (${res.status})`);
    }
}

/**
 * Directly updates an existing Quote document in MongoDB via the REST API.
 * @param {Object} quoteDoc The raw Quote object to update
 * @returns {Promise<Object>} The updated quote document from the API
 */
export async function updateQuoteDoc(quoteDoc, fieldsToUpdate = null) {
    if (!quoteDoc) return null;
    let qId;
    let payload;
    if (typeof quoteDoc === "object") {
        qId = quoteDoc.id || quoteDoc._id;
        payload = fieldsToUpdate ? { ...quoteDoc, ...fieldsToUpdate } : { ...quoteDoc };
    } else {
        qId = quoteDoc;
        payload = { ...(fieldsToUpdate || {}), id: qId };
    }
    if (!qId) return quoteDoc;
    if (!payload.id && !payload._id) payload.id = qId;

    // Eliminate company keys from quote update payload
    delete payload.company;
    delete payload.company_id;

    if (Array.isArray(payload.quote_materials)) {
        payload.quote_materials = payload.quote_materials.map(m => {
            const {
                start_time: _st, finish_time: _ft, load_mins: _lm, tip_mins: _tm,
                runs_per_day: _rpd, total_loads: _tl, estimated_days: _ed,
                days_single_truck: _dst, full_days: _fd, remainder_days: _rd,
                total_tolls: _tt, truck_day_rate: _tdr, day_rate: _dr,
                truck_type: _ttype, truck_capacity_tonne: _tcap,
                trailer_type: _trltype, trailer_capacity_tonne: _trlcap,
                total_capacity_tonne: _totcap,
                ...cleanM
            } = m;

            const sched = cleanM.schedule || {};
            const opHours = cleanM.operating_hours || {};
            const fleet = cleanM.fleet || {};
            const fin = cleanM.financial_breakdown || {};
            const runsPerDay = Number(sched.runs_per_day ?? 5.0);
            const totalLoads = Number(sched.total_loads ?? 1);
            const estDays = Number(sched.estimated_days ?? 1.0);
            const totalTolls = Number(sched.total_tolls ?? 0);
            const truckDayRate = Number(fleet.truck_day_rate ?? 1400);
            const truckCap = Number(fleet.truck_capacity_tonne ?? 19);
            const trailerCap = Number(fleet.trailer_capacity_tonne ?? 19);
            const totalCap = Number(fleet.total_capacity_tonne ?? (truckCap + trailerCap) ?? 38);

            return {
                ...cleanM,
                schedule: {
                    runs_per_day: runsPerDay,
                    total_loads: totalLoads,
                    allocated_trucks: Number(sched.allocated_trucks ?? (Array.isArray(cleanM.allocated_trucks) ? cleanM.allocated_trucks.length : 1)),
                    estimated_days: estDays,
                    days_single_truck: Number(sched.days_single_truck ?? (runsPerDay > 0 ? (totalLoads / runsPerDay).toFixed(2) : 1)),
                    full_days: Number(sched.full_days ?? Math.floor(estDays)),
                    remainder_days: Number(sched.remainder_days ?? 0),
                    total_tolls: totalTolls,
                    ...sched
                },
                operating_hours: {
                    start_time: opHours.start_time ?? "07:00",
                    finish_time: opHours.finish_time ?? "17:00",
                    load_mins: Number(opHours.load_mins ?? 15),
                    tip_mins: Number(opHours.tip_mins ?? 15),
                    ...opHours
                },
                fleet: {
                    truck_type: fleet.truck_type ?? (truckCap === 12 ? "6 wheeler - 12t" : (truckCap === 16 ? "8 wheeler - 16t" : (truckCap === 15 ? "8 wheeler - 15t" : "10 wheeler - 19t"))),
                    truck_capacity_tonne: truckCap,
                    trailer_type: fleet.trailer_type ?? (trailerCap === 0 ? "None" : "8 wheeler - 20t"),
                    trailer_capacity_tonne: trailerCap,
                    total_capacity_tonne: totalCap,
                    truck_day_rate: truckDayRate,
                    ...fleet
                },
                financial_breakdown: {
                    base_disposal_rate: Number(fin.base_disposal_rate ?? (cleanM.cost_per_tonne != null ? Number(cleanM.cost_per_tonne) : 0)),
                    base_cost_per_load: Number(fin.base_cost_per_load ?? 0),
                    base_day_rate: Number(fin.base_day_rate ?? truckDayRate),
                    base_cartage_cost: Number(fin.base_cartage_cost ?? 0),
                    base_tolls: Number(fin.base_tolls ?? totalTolls),
                    base_material_cost: Number(fin.base_material_cost ?? 0),
                    base_total_cost: Number(fin.base_total_cost ?? (cleanM.total_cost != null ? Number(cleanM.total_cost) : 0)),
                    quoted_disposal_rate: Number(fin.quoted_disposal_rate ?? (cleanM.proposed_unit_price != null ? Number(cleanM.proposed_unit_price) : (cleanM.unit_price != null ? Number(cleanM.unit_price) : 0))),
                    quoted_cost_per_load: Number(fin.quoted_cost_per_load ?? 0),
                    quoted_day_rate: Number(fin.quoted_day_rate ?? truckDayRate),
                    quoted_cartage_cost: Number(fin.quoted_cartage_cost ?? 0),
                    quoted_tolls: Number(fin.quoted_tolls ?? totalTolls),
                    subtotal_ex_gst: Number(fin.subtotal_ex_gst ?? (cleanM.proposed_total_price != null ? Number(cleanM.proposed_total_price) : (cleanM.total_price != null ? Number(cleanM.total_price) : 0))),
                    gst: Number(fin.gst ?? 0),
                    total_inc_gst: Number(fin.total_inc_gst ?? 0),
                    effective_rate_per_tonne: Number(fin.effective_rate_per_tonne ?? (cleanM.proposed_unit_price != null ? Number(cleanM.proposed_unit_price) : (cleanM.unit_price != null ? Number(cleanM.unit_price) : 0))),
                    margin_added: Number(fin.margin_added ?? 0),
                    ...fin
                }
            };
        });
    }

    const url = `${QUOTES_API_URL}/quotes/${qId}`;
    const fallbackUrl = `${QUOTES_API_URL}/quotes?id=${qId}`;

    console.group(`%c[Quotes API Request] PUT ${url}`, 'color: #0284c7; font-weight: bold; font-size: 13px;');
    console.log("Updating Quote Document (updateQuoteDoc):", payload);
    if (payload.quote_materials && payload.quote_materials.length > 0) {
        console.table(payload.quote_materials.map((m, idx) => ({
            "#": idx + 1,
            "Material": m.material_name,
            "Quantity": m.quantity,
            "Yard": m.yard?.name || m.dispose_address?.name,
            "Has Polyline": !!m.polyline,
            "Polyline Len": m.polyline ? String(m.polyline).length : 0,
            "Polyline Preview": m.polyline ? (String(m.polyline).substring(0, 35) + "...") : "NULL",
            "Unit Price": m.proposed_unit_price,
            "Total Price": m.proposed_total_price
        })));
    }
    console.groupEnd();

    let res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    }).catch(() => null);

    if (!res || !res.ok) {
        res = await fetch(fallbackUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        console.group(`%c[Quotes API Error] PUT ${url} (HTTP ${res.status})`, 'color: #dc2626; font-weight: bold;');
        console.error("API error response:", data);
        console.groupEnd();
        throw new Error(data.message || data.error || `Failed to update quote (HTTP ${res.status})`);
    }

    console.group(`%c[Quotes API Response] PUT ${url} (HTTP ${res.status} OK)`, 'color: #16a34a; font-weight: bold;');
    console.log("Updated Quote response from MongoDB:", data.quote || data);
    console.groupEnd();

    return data.quote || data;
}

/**
 * Fetches quotes from the API.
 * @param {string|number} [quoteId] Optional quote ID to fetch a single quote
 * @returns {Promise<Array|Object>} Array of quotes or single quote object
 */
export async function fetchQuotes(quoteId = null) {
    let url = `${QUOTES_API_URL}/quotes?populate=customer`;
    if (quoteId) {
        url += `&id=${quoteId}`;
    }
    const res = await fetch(url);
    const resJson = await res.json().catch(() => ({}));
    if (res.ok) {
        return quoteId ? resJson.quote : resJson.quotes;
    } else {
        throw new Error(resJson.message || resJson.error || `Failed to fetch quotes (HTTP ${res.status})`);
    }
}

/**
 * Deletes a quote from the API by ID.
 * @param {string|number} quoteId The quote ID to delete
 * @returns {Promise<Object>} The deletion confirmation
 */
export async function deleteQuote(quoteId) {
    if (!quoteId) throw new Error("Quote ID is required to delete.");
    const url = `${QUOTES_API_URL}/quotes?id=${quoteId}`;
    const res = await fetch(url, { method: 'DELETE' });
    const resJson = await res.json().catch(() => ({}));
    if (res.ok) {
        return resJson;
    } else {
        throw new Error(resJson.message || resJson.error || `Failed to delete quote (HTTP ${res.status})`);
    }
}

/**
 * Converts a Quote into a Job with Materials in the database, optionally passing dispatch parameters.
 * @param {string|number} quoteId The quote ID to convert
 * @param {string} [jobStatus="proposed"] Initial status of generated Job ('proposed', 'active')
 * @param {Object} [dispatchParams={}] Optional dispatch parameters (po_number, start_date, days_count, uhf channels, allocations, etc.)
 * @returns {Promise<Object>} Object containing confirmation message, generated job, and converted quote
 */
export async function generateJobFromQuote(quoteId, jobStatus = "proposed", dispatchParams = {}) {
    if (!quoteId) throw new Error("Quote ID is required to generate a job.");
    const { target_completion_date, company: _comp, company_id: _comp_id, ...cleanDispatchParams } = dispatchParams;
    const payload = {
        quote_id: quoteId,
        id: quoteId,
        status: jobStatus,
        ...cleanDispatchParams
    };

    // Support POST /quotes/{id}/convert with fallback to POST /quotes/generate-job
    const convertUrl = `${QUOTES_API_URL}/quotes/${quoteId}/convert`;
    let res = await fetch(convertUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    }).catch(() => null);

    if (!res || !res.ok) {
        res = await fetch(`${QUOTES_API_URL}/quotes/generate-job`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
    }

    const resJson = await res.json().catch(() => ({}));
    if (res.ok) {
        if (resJson && resJson.job) {
            delete resJson.job.company;
            if (!activeJobs || !Array.isArray(activeJobs.jobs)) {
                activeJobs = { count: 0, jobs: [] };
            }
            activeJobs.jobs = [resJson.job, ...activeJobs.jobs.filter(j => j.id !== resJson.job.id)];
            activeJobs.count = activeJobs.jobs.length;
            activeJobs = { ...activeJobs };
            jobsListStore.set(activeJobs.jobs);
            selectedJobs.update(ids => (ids.includes(resJson.job.id) ? ids : [...ids, resJson.job.id]));
        }
        return resJson;
    } else {
        throw new Error(resJson.message || resJson.error || `Failed to generate job from quote (HTTP ${res.status})`);
    }
}

/**
 * Fetches environment variables from the backend server /env endpoint.
 */
export async function fetchServerEnv() {
    const url = `${TT_WEB_API_URL}/env`;
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error(`Failed to fetch server environment from ${url} (HTTP ${res.status})`);
    }
    return await res.json();
}
